/**
 * FontAwesome Icon Library
 *
 * This file imports only the icons used in the application using deep imports.
 * Deep imports ensure only the specific icon definitions are bundled,
 * reducing bundle size from ~6MB to ~100KB.
 *
 * To add a new icon:
 * 1. Find the icon on https://fontawesome.com/icons
 * 2. Add a deep import from the appropriate package (solid/regular/light/duotone)
 * 3. Add it to the library.add() call at the bottom
 *
 * Icon naming convention:
 * - Import name: camelCase with 'fa' prefix (e.g., faUserPlus)
 * - Usage in components: ['far', 'user-plus'] or 'far fa-user-plus'
 */

import { library } from "@fortawesome/fontawesome-svg-core";
import { faClock as fadClock } from "@fortawesome/pro-duotone-svg-icons/faClock";
import { faExclamationTriangle as fadExclamationTriangle } from "@fortawesome/pro-duotone-svg-icons/faExclamationTriangle";
import { faHandshakeAlt } from "@fortawesome/pro-duotone-svg-icons/faHandshakeAlt";
// ============================================================================
// DUOTONE ICONS (fad)
// ============================================================================
import { faInfoCircle as fadInfoCircle } from "@fortawesome/pro-duotone-svg-icons/faInfoCircle";
import { faTimesCircle as fadTimesCircle } from "@fortawesome/pro-duotone-svg-icons/faTimesCircle";

// ============================================================================
// LIGHT ICONS (fal)
// ============================================================================
import { faAt as falAt } from "@fortawesome/pro-light-svg-icons/faAt";
import { faBolt } from "@fortawesome/pro-light-svg-icons/faBolt";
import { faBooks } from "@fortawesome/pro-light-svg-icons/faBooks";
import { faBriefcase } from "@fortawesome/pro-light-svg-icons/faBriefcase";
import { faCalendarAlt as falCalendarAlt } from "@fortawesome/pro-light-svg-icons/faCalendarAlt";
import { faCloudUpload } from "@fortawesome/pro-light-svg-icons/faCloudUpload";
import { faComment as falComment } from "@fortawesome/pro-light-svg-icons/faComment";
import { faCommentAlt } from "@fortawesome/pro-light-svg-icons/faCommentAlt";
import { faCommentAltExclamation as falCommentAltExclamation } from "@fortawesome/pro-light-svg-icons/faCommentAltExclamation";
import { faCopy as falCopy } from "@fortawesome/pro-light-svg-icons/faCopy";
import { faEmptySet as falEmptySet } from "@fortawesome/pro-light-svg-icons/faEmptySet";
import { faEnvelope as falEnvelope } from "@fortawesome/pro-light-svg-icons/faEnvelope";
import { faExclamationCircle as falExclamationCircle } from "@fortawesome/pro-light-svg-icons/faExclamationCircle";
import { faExclamationTriangle as falExclamationTriangle } from "@fortawesome/pro-light-svg-icons/faExclamationTriangle";
import { faExternalLinkSquareAlt } from "@fortawesome/pro-light-svg-icons/faExternalLinkSquareAlt";
import { faFolderOpen } from "@fortawesome/pro-light-svg-icons/faFolderOpen";
import { faGripVertical } from "@fortawesome/pro-light-svg-icons/faGripVertical";
import { faInboxIn as falInboxIn } from "@fortawesome/pro-light-svg-icons/faInboxIn";
import { faInfoCircle as falInfoCircle } from "@fortawesome/pro-light-svg-icons/faInfoCircle";
import { faLightbulb as falLightbulb } from "@fortawesome/pro-light-svg-icons/faLightbulb";
import { faMagic } from "@fortawesome/pro-light-svg-icons/faMagic";
import { faMicrophone as falMicrophone } from "@fortawesome/pro-light-svg-icons/faMicrophone";
import { faPhoneAlt as falPhoneAlt } from "@fortawesome/pro-light-svg-icons/faPhoneAlt";
import { faSpinner as falSpinner } from "@fortawesome/pro-light-svg-icons/faSpinner";
import { faStickyNote as falStickyNote } from "@fortawesome/pro-light-svg-icons/faStickyNote";
import { faThumbtack as falThumbtack } from "@fortawesome/pro-light-svg-icons/faThumbtack";
import { faTrashUndo } from "@fortawesome/pro-light-svg-icons/faTrashUndo";
import { faVideo } from "@fortawesome/pro-light-svg-icons/faVideo";
// ============================================================================
// REGULAR ICONS (far)
// ============================================================================
import { faAlignLeft } from "@fortawesome/pro-regular-svg-icons/faAlignLeft";
import { faAngleDoubleRight as farAngleDoubleRight } from "@fortawesome/pro-regular-svg-icons/faAngleDoubleRight";
import { faArchive } from "@fortawesome/pro-regular-svg-icons/faArchive";
import { faArrowDown } from "@fortawesome/pro-regular-svg-icons/faArrowDown";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons/faArrowLeft";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons/faArrowRight";
import { faArrowToBottom } from "@fortawesome/pro-regular-svg-icons/faArrowToBottom";
import { faArrowToTop } from "@fortawesome/pro-regular-svg-icons/faArrowToTop";
import { faArrowUp } from "@fortawesome/pro-regular-svg-icons/faArrowUp";
import { faBalanceScale } from "@fortawesome/pro-regular-svg-icons/faBalanceScale";
import { faBan } from "@fortawesome/pro-regular-svg-icons/faBan";
import { faBolt as farBolt } from "@fortawesome/pro-regular-svg-icons/faBolt";
import { faBook } from "@fortawesome/pro-regular-svg-icons/faBook";
import { faBookmark as farBookmark } from "@fortawesome/pro-regular-svg-icons/faBookmark";
import { faBrain } from "@fortawesome/pro-regular-svg-icons/faBrain";
import { faBriefcase as farBriefcase } from "@fortawesome/pro-regular-svg-icons/faBriefcase";
import { faBullhorn as farBullhorn } from "@fortawesome/pro-regular-svg-icons/faBullhorn";
import { faBullseyePointer } from "@fortawesome/pro-regular-svg-icons/faBullseyePointer";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons/faCalendar";
import { faCalendarAlt as farCalendarAlt } from "@fortawesome/pro-regular-svg-icons/faCalendarAlt";
import { faCalendarCheck } from "@fortawesome/pro-regular-svg-icons/faCalendarCheck";
import { faCamera as farCamera } from "@fortawesome/pro-regular-svg-icons/faCamera";
import { faChartBar } from "@fortawesome/pro-regular-svg-icons/faChartBar";
import { faChartLine as farChartLine } from "@fortawesome/pro-regular-svg-icons/faChartLine";
import { faCheck as farCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { faCheckCircle as farCheckCircle } from "@fortawesome/pro-regular-svg-icons/faCheckCircle";
import { faCheckSquare as farCheckSquare } from "@fortawesome/pro-regular-svg-icons/faCheckSquare";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons/faChevronDown";
import { faChevronLeft as farChevronLeft } from "@fortawesome/pro-regular-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons/faChevronRight";
import { faClock } from "@fortawesome/pro-regular-svg-icons/faClock";
import { faCode } from "@fortawesome/pro-regular-svg-icons/faCode";
import { faCog as farCog } from "@fortawesome/pro-regular-svg-icons/faCog";
import { faComment as farComment } from "@fortawesome/pro-regular-svg-icons/faComment";
import { faCopy as farCopy } from "@fortawesome/pro-regular-svg-icons/faCopy";
import { faCreditCard } from "@fortawesome/pro-regular-svg-icons/faCreditCard";
import { faDesktop as farDesktop } from "@fortawesome/pro-regular-svg-icons/faDesktop";
import { faDownload as farDownload } from "@fortawesome/pro-regular-svg-icons/faDownload";
import { faEdit } from "@fortawesome/pro-regular-svg-icons/faEdit";
import { faEllipsisH as farEllipsisH } from "@fortawesome/pro-regular-svg-icons/faEllipsisH";
import { faEllipsisV } from "@fortawesome/pro-regular-svg-icons/faEllipsisV";
import { faEnvelope } from "@fortawesome/pro-regular-svg-icons/faEnvelope";
import { faEnvelopeOpen } from "@fortawesome/pro-regular-svg-icons/faEnvelopeOpen";
import { faExclamationCircle as farExclamationCircle } from "@fortawesome/pro-regular-svg-icons/faExclamationCircle";
import { faExclamationTriangle as farExclamationTriangle } from "@fortawesome/pro-regular-svg-icons/faExclamationTriangle";
import { faExternalLink as farExternalLink } from "@fortawesome/pro-regular-svg-icons/faExternalLink";
import { faExternalLinkAlt as farExternalLinkAlt } from "@fortawesome/pro-regular-svg-icons/faExternalLinkAlt";
import { faEye } from "@fortawesome/pro-regular-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/pro-regular-svg-icons/faEyeSlash";
import { faFile as farFile } from "@fortawesome/pro-regular-svg-icons/faFile";
import { faFileAlt as farFileAlt } from "@fortawesome/pro-regular-svg-icons/faFileAlt";
import { faFileVideo } from "@fortawesome/pro-regular-svg-icons/faFileVideo";
import { faFilter } from "@fortawesome/pro-regular-svg-icons/faFilter";
import { faFolder as farFolder } from "@fortawesome/pro-regular-svg-icons/faFolder";
import { faFrown } from "@fortawesome/pro-regular-svg-icons/faFrown";
import { faHistory } from "@fortawesome/pro-regular-svg-icons/faHistory";
import { faImage } from "@fortawesome/pro-regular-svg-icons/faImage";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons/faInfoCircle";
import { faKey as farKey } from "@fortawesome/pro-regular-svg-icons/faKey";
import { faLifeRing } from "@fortawesome/pro-regular-svg-icons/faLifeRing";
import { faLightbulb } from "@fortawesome/pro-regular-svg-icons/faLightbulb";
import { faLink as farLink } from "@fortawesome/pro-regular-svg-icons/faLink";
import { faListAlt } from "@fortawesome/pro-regular-svg-icons/faListAlt";
import { faLock } from "@fortawesome/pro-regular-svg-icons/faLock";
import { faMicrophone as farMicrophone } from "@fortawesome/pro-regular-svg-icons/faMicrophone";
import { faMinus } from "@fortawesome/pro-regular-svg-icons/faMinus";
import { faMobileAlt } from "@fortawesome/pro-regular-svg-icons/faMobileAlt";
import { faPaperPlane } from "@fortawesome/pro-regular-svg-icons/faPaperPlane";
import { faPen } from "@fortawesome/pro-regular-svg-icons/faPen";
import { faPencil } from "@fortawesome/pro-regular-svg-icons/faPencil";
import { faPencilAlt as farPencilAlt } from "@fortawesome/pro-regular-svg-icons/faPencilAlt";
import { faPhoneAlt as farPhoneAlt } from "@fortawesome/pro-regular-svg-icons/faPhoneAlt";
import { faPhotoVideo } from "@fortawesome/pro-regular-svg-icons/faPhotoVideo";
import { faPlus as farPlus } from "@fortawesome/pro-regular-svg-icons/faPlus";
import { faPlusCircle } from "@fortawesome/pro-regular-svg-icons/faPlusCircle";
import { faPrint } from "@fortawesome/pro-regular-svg-icons/faPrint";
import { faRedo } from "@fortawesome/pro-regular-svg-icons/faRedo";
import { faReply } from "@fortawesome/pro-regular-svg-icons/faReply";
import { faReplyAll } from "@fortawesome/pro-regular-svg-icons/faReplyAll";
import { faSave } from "@fortawesome/pro-regular-svg-icons/faSave";
import { faSearch as farSearch } from "@fortawesome/pro-regular-svg-icons/faSearch";
import { faShare } from "@fortawesome/pro-regular-svg-icons/faShare";
import { faSort } from "@fortawesome/pro-regular-svg-icons/faSort";
import { faSpinner as farSpinner } from "@fortawesome/pro-regular-svg-icons/faSpinner";
import { faStickyNote } from "@fortawesome/pro-regular-svg-icons/faStickyNote";
import { faStopCircle } from "@fortawesome/pro-regular-svg-icons/faStopCircle";
import { faSync as farSync } from "@fortawesome/pro-regular-svg-icons/faSync";
import { faTags as farTags } from "@fortawesome/pro-regular-svg-icons/faTags";
import { faTasks as farTasks } from "@fortawesome/pro-regular-svg-icons/faTasks";
import { faThumbsDown } from "@fortawesome/pro-regular-svg-icons/faThumbsDown";
import { faThumbsUp } from "@fortawesome/pro-regular-svg-icons/faThumbsUp";
import { faThumbtack as farThumbtack } from "@fortawesome/pro-regular-svg-icons/faThumbtack";
import { faTimes as farTimes } from "@fortawesome/pro-regular-svg-icons/faTimes";
import { faTimesCircle } from "@fortawesome/pro-regular-svg-icons/faTimesCircle";
import { faTrash } from "@fortawesome/pro-regular-svg-icons/faTrash";
import { faTrashAlt } from "@fortawesome/pro-regular-svg-icons/faTrashAlt";
import { faUndo } from "@fortawesome/pro-regular-svg-icons/faUndo";
import { faUpload as farUpload } from "@fortawesome/pro-regular-svg-icons/faUpload";
import { faUser as farUser } from "@fortawesome/pro-regular-svg-icons/faUser";
import { faUserPlus } from "@fortawesome/pro-regular-svg-icons/faUserPlus";
import { faUsers as farUsers } from "@fortawesome/pro-regular-svg-icons/faUsers";
import { faVideo as farVideo } from "@fortawesome/pro-regular-svg-icons/faVideo";
import { faVideoPlus as farVideoPlus } from "@fortawesome/pro-regular-svg-icons/faVideoPlus";
import { faVideoSlash } from "@fortawesome/pro-regular-svg-icons/faVideoSlash";

// ============================================================================
// SOLID ICONS (fas)
// ============================================================================
import { faAddressBook } from "@fortawesome/pro-solid-svg-icons/faAddressBook";
import { faAngleDoubleRight } from "@fortawesome/pro-solid-svg-icons/faAngleDoubleRight";
import { faAngleDown } from "@fortawesome/pro-solid-svg-icons/faAngleDown";
import { faBell } from "@fortawesome/pro-solid-svg-icons/faBell";
import { faBolt as fasBolt } from "@fortawesome/pro-solid-svg-icons/faBolt";
import { faBuilding } from "@fortawesome/pro-solid-svg-icons/faBuilding";
import { faBullhorn } from "@fortawesome/pro-solid-svg-icons/faBullhorn";
import { faCalculator } from "@fortawesome/pro-solid-svg-icons/faCalculator";
import { faCalendarAlt } from "@fortawesome/pro-solid-svg-icons/faCalendarAlt";
import { faCalendarDay } from "@fortawesome/pro-solid-svg-icons/faCalendarDay";
import { faCaretDown } from "@fortawesome/pro-solid-svg-icons/faCaretDown";
import { faCaretLeft } from "@fortawesome/pro-solid-svg-icons/faCaretLeft";
import { faCaretUp } from "@fortawesome/pro-solid-svg-icons/faCaretUp";
import { faChartLine } from "@fortawesome/pro-solid-svg-icons/faChartLine";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons/faChevronLeft";
import { faCircle } from "@fortawesome/pro-solid-svg-icons/faCircle";
import { faCog } from "@fortawesome/pro-solid-svg-icons/faCog";
import { faComment } from "@fortawesome/pro-solid-svg-icons/faComment";
import { faCompress } from "@fortawesome/pro-solid-svg-icons/faCompress";
import { faCopy } from "@fortawesome/pro-solid-svg-icons/faCopy";
import { faDatabase } from "@fortawesome/pro-solid-svg-icons/faDatabase";
import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons/faEllipsisH";
import { faEnvelope as fasEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { faExchange } from "@fortawesome/pro-solid-svg-icons/faExchange";
import { faExchangeAlt } from "@fortawesome/pro-solid-svg-icons/faExchangeAlt";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle";
import { faExpand } from "@fortawesome/pro-solid-svg-icons/faExpand";
import { faExternalLink } from "@fortawesome/pro-solid-svg-icons/faExternalLink";
import { faExternalLinkAlt } from "@fortawesome/pro-solid-svg-icons/faExternalLinkAlt";
import { faFile } from "@fortawesome/pro-solid-svg-icons/faFile";
import { faFileAlt } from "@fortawesome/pro-solid-svg-icons/faFileAlt";
import { faInbox } from "@fortawesome/pro-solid-svg-icons/faInbox";
import { faKey } from "@fortawesome/pro-solid-svg-icons/faKey";
import { faMicrophone } from "@fortawesome/pro-solid-svg-icons/faMicrophone";
import { faMicrophoneSlash } from "@fortawesome/pro-solid-svg-icons/faMicrophoneSlash";
import { faMinus as fasMinus } from "@fortawesome/pro-solid-svg-icons/faMinus";
import { faPause } from "@fortawesome/pro-solid-svg-icons/faPause";
import { faPencilAlt } from "@fortawesome/pro-solid-svg-icons/faPencilAlt";
import { faPhoneAlt } from "@fortawesome/pro-solid-svg-icons/faPhoneAlt";
import { faPlay } from "@fortawesome/pro-solid-svg-icons/faPlay";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons/faQuestionCircle";
import { faRedo as fasRedo } from "@fortawesome/pro-solid-svg-icons/faRedo";
import { faSearch } from "@fortawesome/pro-solid-svg-icons/faSearch";
import { faShareSquare } from "@fortawesome/pro-solid-svg-icons/faShareSquare";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons/faSpinner";
import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { faSync } from "@fortawesome/pro-solid-svg-icons/faSync";
import { faTags } from "@fortawesome/pro-solid-svg-icons/faTags";
import { faTasks } from "@fortawesome/pro-solid-svg-icons/faTasks";
import { faThumbtack } from "@fortawesome/pro-solid-svg-icons/faThumbtack";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { faUpload } from "@fortawesome/pro-solid-svg-icons/faUpload";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import { faUserMinus } from "@fortawesome/pro-solid-svg-icons/faUserMinus";
import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";
import { faUserTag } from "@fortawesome/pro-solid-svg-icons/faUserTag";
import { faVideoPlus } from "@fortawesome/pro-solid-svg-icons/faVideoPlus";
import { faVoicemail } from "@fortawesome/pro-solid-svg-icons/faVoicemail";

// Add all icons to the library
library.add(
  // Solid (fas)
  faAddressBook,
  faAngleDoubleRight,
  faAngleDown,
  faBell,
  fasBolt,
  faBuilding,
  faBullhorn,
  faCalculator,
  faCalendarAlt,
  faCalendarDay,
  faCaretDown,
  faCaretLeft,
  faCaretUp,
  faChartLine,
  faCheck,
  faCheckCircle,
  faChevronLeft,
  faCircle,
  faCog,
  faComment,
  faCompress,
  faCopy,
  faDatabase,
  faDownload,
  faEllipsisH,
  fasEnvelope,
  faExchange,
  faExchangeAlt,
  faExpand,
  faExclamationCircle,
  faExclamationTriangle,
  faExternalLink,
  faExternalLinkAlt,
  faFile,
  faFileAlt,
  faInbox,
  faKey,
  faMicrophone,
  faMicrophoneSlash,
  fasMinus,
  faPause,
  faPencilAlt,
  faPhoneAlt,
  faPlay,
  faPlus,
  faQuestionCircle,
  fasRedo,
  faSearch,
  faShareSquare,
  faSpinner,
  faStar,
  faSync,
  faTags,
  faTasks,
  faThumbtack,
  faTimes,
  faUpload,
  faUser,
  faUserMinus,
  faUserTag,
  faUsers,
  faVideoPlus,
  faVoicemail,

  // Regular (far)
  farAngleDoubleRight,
  faArrowDown,
  faArrowToBottom,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
  faArrowToTop,
  faAlignLeft,
  faBalanceScale,
  faBan,
  farBookmark,
  farBolt,
  faBook,
  faBrain,
  farBriefcase,
  farBullhorn,
  faBullseyePointer,
  faCalendar,
  farCalendarAlt,
  faCalendarCheck,
  farCamera,
  faChartBar,
  farChartLine,
  farCheck,
  farCheckCircle,
  farCheckSquare,
  farChevronLeft,
  faChevronDown,
  faChevronRight,
  faClock,
  faCode,
  farCog,
  farComment,
  farCopy,
  faCreditCard,
  farDownload,
  farDesktop,
  faEdit,
  farEllipsisH,
  faEllipsisV,
  faEnvelope,
  faEnvelopeOpen,
  faArchive,
  farExclamationCircle,
  farExclamationTriangle,
  farExternalLinkAlt,
  farExternalLink,
  faEye,
  faEyeSlash,
  farFile,
  faFilter,
  farFileAlt,
  faFileVideo,
  faFrown,
  farFolder,
  faHistory,
  faImage,
  faInfoCircle,
  farKey,
  faLightbulb,
  faLifeRing,
  farLink,
  faListAlt,
  faLock,
  farMicrophone,
  faMinus,
  faMobileAlt,
  faPaperPlane,
  faPen,
  faPencil,
  farPencilAlt,
  farPhoneAlt,
  faPhotoVideo,
  farPlus,
  faPlusCircle,
  faPrint,
  faRedo,
  faReply,
  faReplyAll,
  faSave,
  farSearch,
  faShare,
  faSort,
  farSpinner,
  faStopCircle,
  faStickyNote,
  farSync,
  farTags,
  farTasks,
  farThumbtack,
  faThumbsDown,
  faThumbsUp,
  farTimes,
  faTimesCircle,
  faTrash,
  faTrashAlt,
  faUndo,
  farUpload,
  farUser,
  faUserPlus,
  farUsers,
  farVideo,
  farVideoPlus,
  faVideoSlash,

  // Light (fal)
  falAt,
  faBolt,
  faBooks,
  faBriefcase,
  falCalendarAlt,
  faCloudUpload,
  falComment,
  faCommentAlt,
  falCommentAltExclamation,
  falCopy,
  falEmptySet,
  falEnvelope,
  falExclamationCircle,
  falExclamationTriangle,
  faExternalLinkSquareAlt,
  faFolderOpen,
  faGripVertical,
  falInboxIn,
  falInfoCircle,
  falLightbulb,
  faMagic,
  falMicrophone,
  falPhoneAlt,
  falSpinner,
  falStickyNote,
  falThumbtack,
  faTrashUndo,
  faVideo,
  // Duotone (fad)
  fadClock,
  fadExclamationTriangle,
  faHandshakeAlt,
  fadInfoCircle,
  fadTimesCircle,
);
